/**
 * Slider Gesture
 *
 * @package felixkiesewetter-js/slider
 * @since 1.13.0
 * @author Felix Kiesewetter <info@felixkiesewetter.de>
 * @version 2.0.0
 * @lastModified 2025-12-10 Improved scroll vs. swipe lock, used TS.
 */
class SliderGesture
{

    private direction :number;
    private distance :{} = { x: 0, y: 0 };
    private duration :number;
    private inProgress :boolean;
    private interval :number;
    private node :Node;
    private pointerPosition :{} = {
        start:   { x: 0, y: 0 },
        current: { x: 0, y: 0 },
        last:    { x: 0, y: 0 }
    };
    private preventActions :boolean;
    private scrollPosition :{} = {
        start:   { x: 0, y: 0 },
        current: { x: 0, y: 0 }
    };
    private settings :{};

    /**
     * Constructor.
     */
    constructor(node :Node = null, options :Object = {})
    {
        this.node = node;
        this.setSettings(options);
        this.reset();
        this.init();
    }

    /**
     * Initialize touch and mouse events.
     */
    init() :void
    {
        // Buttons and links should not fire if swipe is in progress
        this.node.querySelectorAll(this.settings.preventActions)?.forEach((a) => {
            a.addEventListener('click', (e :Event) => {
                if (this.preventActions) {
                    this.settings.debug && console.warn('[SwipeGesture]', 'User action prevented while swiping');
                    e.preventDefault();
                }
            });
        });

        // Init gestures
        this.settings.mouse && this.node.addEventListener('mousedown', (e) => this.start(e), false);
        this.node.addEventListener('touchstart', (e) => this.start(e), { passive: false, capture : true });
        this.settings.mouse ? this.node.addEventListener('mousemove', (e) => this.move(e), false) : null;
        this.node.addEventListener('touchmove', (e) => this.move(e), { passive: false, capture : true });
        this.settings.mouse ? this.node.addEventListener('mouseup', (e) => this.release(e), false) : null;
        this.node.addEventListener('touchend', (e) => this.release(e), { passive: false, capture : true });

        // Certain window events should release swipe
        this.settings.mouse && window.addEventListener('mousedown', (e) => this.release(e));
        window.addEventListener('contextmenu', (e) => this.release(e));

        // Disable drag functionality for some elements
        if (this.settings.preventDrag) {
            this.node.querySelectorAll(this.settings.preventDrag)?.forEach((d) => {
                d.classList.add('-prevent-drag');
                d.setAttribute('draggable', false);
                d.addEventListener('dragstart', (e) => {
                    e.preventDefault();
                    return false;
                });
            });
        }

        // Disable select functionality for some elements
        if (this.settings.preventSelect) {
            this.node.querySelectorAll(this.settings.preventSelect)?.forEach((s) => {
                s.classList.add('-prevent-select');
                s.setAttribute('unselectable', 'on');
            });
        }
    }

    /**
     * Start a swipe.
     */
    start(e :Event) :boolean
    {
        const event = e.originalEvent || e;
        event.stopPropagation();
        if (this.inProgress || ('undefined' !== typeof event.button && this.settings.mouseMainButtonOnly && 0 !== event.button)) {
            return false;
        }
        this.inProgress = true;
        this.preventActions = true;
        this.pointerPosition.start.x = this.getMousePosition(event).client.x || event.touches[0].pageX;
        this.pointerPosition.start.y = this.getMousePosition(event).client.y || event.touches[0].pageY;
        this.pointerPosition.last = this.pointerPosition.start;
        this.scrollPosition.start.x = window.scrollX;
        this.scrollPosition.start.y = window.scrollY;
        this.interval = setInterval(() => {
                this.duration += 10;
            }, 10
        );
        if (typeof this.settings.onStart === 'function') {
            this.settings.debug && console.log('[SwipeGesture]', 'start');
            this.settings.onStart();
        }
        return true;
    }

    /**
     * Move/drag on the node.
     */
    move(e :Event) :void
    {
        const event :Event = e.originalEvent || e;
        if (!this.inProgress) {
            return;
        }
        this.scrollPosition.current.x = window.scrollX;
        this.scrollPosition.current.y = window.scrollY;
        this.pointerPosition.current.x = this.getMousePosition(event).client.x || event.touches[0].pageX;
        this.pointerPosition.current.y = this.getMousePosition(event).client.y || event.touches[0].pageY;
        this.distance.x = Math.round(Math.abs(this.pointerPosition.current.x - this.pointerPosition.start.x));
        this.distance.y = Math.round(Math.abs(this.pointerPosition.current.y - this.pointerPosition.start.y));
        if (
            (
                SliderGesture.AXIS_X === this.settings.limitToAxis &&
                (
                    this.scrollPosition.current.y !== this.scrollPosition.start.y ||
                    this.distance.x <= this.settings.deadzone
                )
            ) ||
            (
                SliderGesture.AXIS_Y === this.settings.limitToAxis &&
                (
                    this.scrollPosition.current.x !== this.scrollPosition.start.x ||
                    this.distance.y <= this.settings.deadzone
                )
            )
        ) {
            return;
        }
        if (this.pointerPosition.current.x !== this.pointerPosition.last.x) {
            this.direction = (this.pointerPosition.current.x - this.pointerPosition.start.x <= 0 ? -1 : 1) * (this.settings.inverseX ? 1 : -1);
        }
        const initialDirection = (Math.sign(this.pointerPosition.current.x - this.pointerPosition.start.x) || 1) * (this.settings.inverseX ? 1 : -1);
        const dragX = -1 * initialDirection * this.distance.x;
        if (typeof this.settings.onMove === 'function') {
            this.settings.debug && console.log('[SwipeGesture]', 'move');
            this.settings.onMove({
                distance: this.distance,
                duration: this.duration,
                direction: this.direction
            });
        }
        this.node.style.setProperty('--dragX', `${dragX}px`);
        this.node.classList.add('-dragging');
        this.pointerPosition.last = this.scrollPosition.current;
        this.scrollPosition.last = this.scrollPosition.current;
    }

    /**
     * Release swipe.
     */
    release(e :Event) :boolean
    {
        if (!this.inProgress) {
            return true;
        }
        this.settings.debug && console.log('[SwipeGesture]', 'release');

        // Detect if swipe is fired
        const speed = this.distance.x / this.duration;
        const fire = speed > this.settings.speedThreshold || this.distance.x >= this.node.offsetWidth * this.settings.distanceThreshold;

        // Stop preventing actions if fired or if threshold was exceeded
        if (fire || this.settings.preventActionsThreshold <= this.distance.x) {
            clearInterval(this.preventActionsTimeout);
            this.preventActionsTimeout = setTimeout(() => {
                this.preventActions = false;
                delete(this.preventActionsTimeout);
            }, this.settings.preventActionsTimeout);
        }
        else if ('number' !== typeof this.preventActionsTimeout) {
            this.preventActions = false;
        }

        // Callback
        if (typeof this.settings.onRelease === 'function') {
            this.settings.onRelease({
                distance: this.distance,
                direction: this.direction,
                fire: fire,
                speed: speed || 0
            });
        }

        // Reset
        this.reset();
    }

    /**
     * Reset.
     */
    reset() :void
    {
        this.settings.debug && console.log('[SwipeGesture]', 'reset');
        this.node.style.setProperty('--dragX', '0px');
        this.node.classList.remove('-dragging');
        this.inProgress = false;
        this.pointerPosition.current.x = 0;
        this.pointerPosition.current.y = 0;
        this.pointerPosition.last.x = 0;
        this.pointerPosition.last.y = 0;
        this.pointerPosition.start.x = 0;
        this.pointerPosition.start.y = 0;
        this.distance.x = 0;
        this.distance.y = 0;
        this.duration = 0;
        clearTimeout(this.timeout);
        clearInterval(this.interval);
        if (typeof this.settings.onReset === 'function') {
            this.settings.onReset();
        }
    }

    /**
     * Set settings.
     */
    setSettings(options :{}) :void
    {
        const defaultSettings = {
            deadzone: 10,
            debug: false,
            mouse: true,
            mouseMainButtonOnly: true,
            speedThreshold: 1,
            distanceThreshold: .5,
            inverseX: false,
            inverseY: false, // TODO: yet without any effect
            limitToAxis: SliderGesture.AXIS_X,
            preventSelect: 'p, h1, h2, h3, h4, h5, h6, span, em, i',
            preventDrag: 'img, a',
            preventActions: 'a, button, [type="submit"]',
            preventActionsThreshold: 25,
            preventActionsTimeout: 1000,
            onStart: () :void => {
                // do stuff
            },
            onMove: (params :{}) :void => {
                // do stuff
            },
            onRelease: (params :{}) :void => {
                // do stuff
            },
            onReset: () :void => {
                // do stuff
            },
        };
        this.settings = {...defaultSettings, ...options};
        this.settings.debug && console.log('[SwipeGesture]', this.settings);
    }

    /**
     * Get mouse position
     *
     * @param {event} event
     * @return {object}
     */
    getMousePosition(event)
    {
        let pageX = event.pageX;
        let pageY = event.pageY;
        if (pageX === undefined) {
            pageX = event.clientX + document.body.scrollLeft + document.documentElement.scrollLeft;
            pageY = event.clientY + document.body.scrollTop + document.documentElement.scrollTop;
        }
        const rect = event.target.getBoundingClientRect();
        const offsetX = event.clientX - rect.left;
        const offsetY = event.clientY - rect.top;
        return {
            client: { x: event.clientX, y: event.clientY }, // relative to the viewport
            screen: { x: event.screenX, y: event.screenY }, // relative to the physical screen
            offset: { x: offsetX, y: offsetY },             // relative to the event target
            page:   { x: pageX, y: pageY }                  // relative to the html document
        }
    }

}

Object.defineProperty(SliderGesture, 'AXIS_X', {
    value: 'x'
});

Object.defineProperty(SliderGesture, 'AXIS_Y', {
    value: 'y'
});

export default SliderGesture;
